/**
 * iter-232 Phase 11 — Razorpay Reconciliation Dashboard.
 *
 * Two surfaces on one page:
 *   1. Run reconciliation NOW (admin button → POST /reconciliation/run-now).
 *      Returns stats: orders_checked, transfers_seen, utrs_recorded,
 *      drifts_flagged, errors.
 *   2. Browse unresolved drift alerts from `admin_reconciliation_alerts`
 *      (GET /reconciliation/recent-alerts). Each alert names a
 *      transfer that Razorpay reports as `failed` and our DB likely
 *      thinks succeeded — admin should investigate and either
 *      retry, escalate, or mark resolved.
 *
 * The weekly Mondays-4am cron auto-populates this. This page lets
 * admin trigger reconciliation outside the schedule and see the
 * resulting alerts without diving into MongoDB.
 */
import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Loader2, RefreshCw, PlayCircle, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const AdminReconciliation = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [running, setRunning] = useState(false);
  const [daysBack, setDaysBack] = useState(7);
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  const loadAlerts = async () => {
    setLoadingAlerts(true);
    try {
      const res = await apiCall('/api/admin/razorpay/reconciliation/recent-alerts?limit=100');
      setAlerts(res?.items || []);
    } catch (err) {
      toast({
        title: 'Failed to load alerts',
        description: err?.message || 'Network error',
        variant: 'destructive',
      });
    } finally {
      setLoadingAlerts(false);
    }
  };

  useEffect(() => { loadAlerts(); }, []);

  const runNow = async () => {
    setRunning(true);
    setStats(null);
    try {
      const res = await apiCall(
        `/api/admin/razorpay/reconciliation/run-now?days_back=${daysBack}&max_orders=500`,
        { method: 'POST' },
      );
      setStats(res);
      toast({
        title: 'Reconciliation complete',
        description: `${res.orders_checked ?? 0} orders · ${res.transfers_seen ?? 0} transfers · ${res.utrs_recorded ?? 0} UTRs · ${res.drifts_flagged ?? 0} drifts`,
      });
      await loadAlerts();
    } catch (err) {
      toast({
        title: 'Reconciliation failed',
        description: err?.message || 'Network error',
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <DashboardLayout>
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-reconciliation-page">
      <div>
        <h1 className="text-2xl font-semibold">Razorpay Reconciliation</h1>
        <p className="text-sm text-gray-500 mt-1">
          Weekly cron runs Mondays 4 AM. Manual trigger and drift alerts below.
        </p>
      </div>

      {/* Run-now panel */}
      <Card data-testid="reconciliation-run-now-card">
        <CardHeader>
          <CardTitle className="text-base">Run reconciliation now</CardTitle>
          <CardDescription>
            Walks recent orders, fetches Razorpay&apos;s view of transfers + settlements
            (with UTRs via <code>expand[]=recipient_settlement</code>), persists
            locally, and flags drift.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="days-back">Days back</Label>
              <Input
                id="days-back"
                type="number"
                min="1"
                max="90"
                value={daysBack}
                onChange={(e) => setDaysBack(Math.max(1, Math.min(90, parseInt(e.target.value || '7', 10))))}
                className="w-24"
                disabled={running}
                data-testid="reconciliation-days-back"
              />
            </div>
            <Button onClick={runNow} disabled={running} data-testid="reconciliation-run-btn">
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
              Run now
            </Button>
          </div>

          {stats && (
            <Alert variant={stats.errors > 0 ? 'destructive' : 'default'} data-testid="reconciliation-result">
              {stats.errors > 0 ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              <AlertTitle>Last run</AlertTitle>
              <AlertDescription>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-2 text-sm">
                  <Stat label="Orders" value={stats.orders_checked} />
                  <Stat label="Transfers" value={stats.transfers_seen} />
                  <Stat label="UTRs recorded" value={stats.utrs_recorded} />
                  <Stat label="Drifts flagged" value={stats.drifts_flagged} />
                  <Stat label="Errors" value={stats.errors} accent={stats.errors > 0 ? 'red' : null} />
                </div>
                {stats.ran_at && <p className="text-xs text-gray-500 mt-2">ran at {stats.ran_at}</p>}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Alerts list */}
      <Card data-testid="reconciliation-alerts-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Unresolved drift alerts</CardTitle>
              <CardDescription>
                Transfers where Razorpay&apos;s reported status diverges from our local records.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadAlerts}
              disabled={loadingAlerts}
              data-testid="reconciliation-refresh-alerts"
            >
              {loadingAlerts ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingAlerts && alerts.length === 0 ? (
            <p className="text-sm text-gray-500">Loading alerts…</p>
          ) : alerts.length === 0 ? (
            <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="reconciliation-no-alerts">
              <CheckCircle2 className="h-4 w-4" /> No unresolved drift alerts.
            </p>
          ) : (
            <div className="space-y-2" data-testid="reconciliation-alerts-list">
              {alerts.map((a) => (
                <AlertRow key={a.transfer_id || `${a.flagged_at}-${a.alert_type}`} alert={a} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className={`rounded border p-2 bg-white ${accent === 'red' ? 'border-red-300' : ''}`}>
    <p className="text-xs text-gray-500 uppercase">{label}</p>
    <p className={`text-lg font-semibold ${accent === 'red' ? 'text-red-700' : ''}`}>{value ?? 0}</p>
  </div>
);

const AlertRow = ({ alert }) => (
  <div
    className="rounded border border-amber-200 bg-amber-50 p-3 text-sm"
    data-testid={`reconciliation-alert-${alert.transfer_id || 'row'}`}
  >
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-700" />
        <Badge variant="outline" className="bg-amber-100 text-amber-800">
          {alert.alert_type || 'drift'}
        </Badge>
        {alert.transfer_id && (
          <code className="text-xs bg-white px-1 py-0.5 rounded">{alert.transfer_id}</code>
        )}
      </div>
      <span className="text-xs text-gray-500">{alert.flagged_at}</span>
    </div>
    <div className="text-xs text-gray-700 mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
      {alert.order_id && (
        <span>
          order: <code>{alert.order_id}</code>
          {alert.razorpay_order_id && (
            <a
              href={`https://dashboard.razorpay.com/app/orders/${alert.razorpay_order_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 inline-flex items-center gap-0.5 text-blue-600 underline"
            >
              <ExternalLink className="h-3 w-3" /> Razorpay
            </a>
          )}
        </span>
      )}
      {alert.razorpay_error && (
        <span className="font-mono text-xs text-red-700">
          {typeof alert.razorpay_error === 'string'
            ? alert.razorpay_error
            : alert.razorpay_error?.description || alert.razorpay_error?.code || JSON.stringify(alert.razorpay_error)}
        </span>
      )}
    </div>
  </div>
);

export default AdminReconciliation;
